import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  useTheme,
  useMediaQuery,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Group as GroupIcon,
  Home as HomeIcon,
  Assignment as AssignmentIcon,
  Analytics as AnalyticsIcon,
  Notifications as NotificationsIcon,
  Logout as LogoutIcon,
  PersonAdd as PersonAddIcon,
  Work as WorkIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { hasAnyRole } from '../utils/auth';

const Navigation = ({ onLogout, currentUser }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();

  // Get user information from props (JWT authentication)
  const userRole = currentUser?.role || 'user';
  const userName = currentUser?.preferredName || currentUser?.firstName || currentUser?.email || 'User';

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Users', icon: <PeopleIcon />, path: '/users', staffOnly: true },
    { text: 'Groups', icon: <GroupIcon />, path: '/groups' },
    { text: 'Households', icon: <HomeIcon />, path: '/households', staffOnly: true },
    { text: 'Roles', icon: <AssignmentIcon />, path: '/roles', staffOnly: true },
    { text: 'Role Applications', icon: <WorkIcon />, path: '/role-applications', adminOnly: true },
    { text: 'Team Applications', icon: <AssignmentIcon />, path: '/team-applications', teamSupervisorAndAbove: true },
    { text: 'Team Seeking', icon: <AssignmentIcon />, path: '/team-seeking' },
    { text: 'Rosters', icon: <AssignmentIcon />, path: '/rosters' },
    { text: 'Locations', icon: <LocationIcon />, path: '/admin/locations', adminOnly: true },
    { text: 'Analytics', icon: <AnalyticsIcon />, path: '/analytics', staffOnly: true },
    { text: 'Communications', icon: <NotificationsIcon />, path: '/communications', staffOnly: true },
    { text: 'Scheduler', icon: <AssignmentIcon />, path: '/scheduler' },
  ];

  // Filter menu items based on user role
  console.log('Navigation debug:', { currentUser, userRole, userName });
  
  const filteredMenuItems = menuItems.filter(item => {
    // Check admin-only items (Coach role)
    if (item.adminOnly && userRole !== 'coach') {
      console.log(`Filtering out ${item.text} - adminOnly but userRole is ${userRole}`);
      return false;
    }
    
    // Check staff-only items (Staff or Coach roles)
    if (item.staffOnly && !['staff', 'coach'].includes(userRole)) {
      console.log(`Filtering out ${item.text} - staffOnly but userRole is ${userRole}`);
      return false;
    }
    
    // Check team supervisor and above items (Team Supervisor, Coach, or Staff roles)
    if (item.teamSupervisorAndAbove && !['team_supervisor', 'coach', 'staff'].includes(userRole)) {
      console.log(`Filtering out ${item.text} - teamSupervisorAndAbove but userRole is ${userRole}`);
      return false;
    }
    
    return true;
  });
  
  console.log('Filtered menu items:', filteredMenuItems);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  const handleLogout = () => {
    // Close mobile drawer if open
    setDrawerOpen(false);
    if (onLogout) {
      onLogout();
    }
  };

  const handleSignUp = () => {
    navigate('/');
  };

  const drawer = (
    <Box sx={{ width: 250 }}>
      <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h6" color="primary">
          RoboJackets
        </Typography>
        <Typography variant="caption" color="text.secondary">
          HR Dashboard
        </Typography>
      </Box>
      <List>
        {filteredMenuItems.map((item) => (
                  <ListItem
          key={item.text}
          onClick={() => handleNavigation(item.path)}
          selected={location.pathname === item.path}
          sx={{
            cursor: 'pointer',
            '&.Mui-selected': {
              backgroundColor: theme.palette.primary.light,
              '&:hover': {
                backgroundColor: theme.palette.primary.light,
              },
            },
          }}
        >
            <ListItemIcon sx={{ color: location.pathname === item.path ? 'primary.main' : 'inherit' }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItem>
        ))}
        <Divider sx={{ my: 1 }} />
        <ListItem onClick={handleSignUp} sx={{ cursor: 'pointer' }}>
          <ListItemIcon>
            <PersonAddIcon />
          </ListItemIcon>
          <ListItemText primary="User Registration" />
        </ListItem>
        <ListItem onClick={handleLogout} sx={{ cursor: 'pointer' }}>
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar position="static" elevation={1}>
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            RoboJackets Crew Dashboard
            {userRole && (
              <Typography variant="caption" display="block" sx={{ opacity: 0.8, color: 'white' }}>
                {userRole === 'crew' ? 'Crew Member' : 
                 userRole === 'team_supervisor' ? 'Team Supervisor' : 'User'}
                {userName && ` - ${userName}`}
              </Typography>
            )}
          </Typography>
          
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {filteredMenuItems.map((item) => (
                <Button
                  key={item.text}
                  color="inherit"
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    backgroundColor: location.pathname === item.path ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    },
                  }}
                >
                  {item.text}
                </Button>
              ))}
              <Button
                color="inherit"
                onClick={handleSignUp}
                startIcon={<PersonAddIcon />}
              >
                Registration
              </Button>
              <Button
                color="inherit"
                onClick={handleLogout}
                startIcon={<LogoutIcon />}
              >
                Logout
              </Button>
            </Box>
          )}
          
          {isMobile && (
            <IconButton color="inherit">
              <NotificationsIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>
      
      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 250 },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Navigation; 